import moment from "moment";

export const CAMPAIGN_WIZARD_STEPS = [
  { key: "select", label: "Select Template" },
  { key: "configure", label: "Fill Details & Send" },
];

export const CAMPAIGN_TEMPLATE_STATUS_OPTIONS = [
  { value: "APPROVED", label: "Approved" },
  { value: "PENDING", label: "Pending" },
  { value: "REJECTED", label: "Rejected" },
  { value: "", label: "All statuses" },
];

export const CAMPAIGN_TEMPLATE_PAGE_SIZE = 10;

export const CAMPAIGN_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "FAILED", label: "Failed" },
];

export const CAMPAIGN_PLACEHOLDER_TOKENS = [
  "{{{name}}}",
  "{{{mobile}}}",
  "{{{var1}}}",
  "{{{var2}}}",
  "{{{var3}}}",
  "{{{var4}}}",
  "{{{var5}}}",
];

export function campaignStatusClass(status) {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETED") return "wa-status-completed";
  if (s === "IN_PROGRESS") return "wa-status-progress";
  if (s === "FAILED") return "wa-status-failed";
  if (s === "PENDING") return "wa-status-pending";
  return "wa-status-default";
}

export function formatCampaignDate(value) {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD MMM YYYY, h:mm A") : String(value);
}

export function formatCampaignDateShort(value) {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD MMM YY") : String(value);
}

export function formatCampaignDateMedium(value) {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("MMM DD, YYYY") : String(value);
}

export function formatCampaignDateList(value) {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY, HH:mm:ss") : String(value);
}

export function campaignProgressPercent(campaign) {
  const total =
    Number(campaign?.totalContacts ?? campaign?.total_contacts) || 0;
  if (!total) return 0;
  const sent = Number(campaign?.sentCount ?? campaign?.sent_count) || 0;
  const failed = Number(campaign?.failedCount ?? campaign?.failed_count) || 0;
  return Math.min(100, Math.round(((sent + failed) / total) * 100));
}

export function campaignProgressLabel(campaign) {
  const total =
    Number(campaign?.totalContacts ?? campaign?.total_contacts) || 0;
  const sent = Number(campaign?.sentCount ?? campaign?.sent_count) || 0;
  const failed = Number(campaign?.failedCount ?? campaign?.failed_count) || 0;
  const processed = sent + failed;
  const pct = campaignProgressPercent(campaign);
  return `${pct}% (${processed}/${total})`;
}

export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function getTemplateComponents(row) {
  if (Array.isArray(row?.components)) return row.components;
  return [];
}

export function getCampaignTemplateName(row) {
  return row?.name || row?.template_name || row?.id || "—";
}

export function getCampaignTemplateLanguage(row) {
  return row?.language || row?.template_language || "en";
}

export function getCampaignTemplateCategory(row) {
  return row?.category || "UTILITY";
}

export function getCampaignTemplateStatus(row) {
  return row?.status || "APPROVED";
}

export function getCampaignTemplateBodyText(row) {
  const body = getTemplateComponents(row).find(
    (c) => String(c?.type || "").toUpperCase() === "BODY",
  );
  return body?.text || row?.body || "";
}

export function getCampaignTemplateHeaderComponent(row) {
  return (
    getTemplateComponents(row).find(
      (c) => String(c?.type || "").toUpperCase() === "HEADER",
    ) || null
  );
}

export function getCampaignTemplateHeaderFormat(row) {
  const header = getCampaignTemplateHeaderComponent(row);
  if (!header) return null;
  const format = String(header.format || "").toUpperCase();
  return format && format !== "NONE" ? format : null;
}

/** Map WhatsApp HEADER.format to local media type key used by validators. */
export function mediaTypeFromHeaderFormat(format) {
  const f = String(format || "").toUpperCase();
  if (f === "IMAGE") return "image";
  if (f === "VIDEO") return "video";
  if (f === "DOCUMENT") return "document";
  return null;
}

export function campaignTemplateHasMediaHeader(row) {
  return Boolean(mediaTypeFromHeaderFormat(getCampaignTemplateHeaderFormat(row)));
}

/** Sample / approval media URL if Meta returned an http(s) handle. */
export function getCampaignTemplateSampleMediaUrl(row) {
  const header = getCampaignTemplateHeaderComponent(row);
  const handles = header?.example?.header_handle;
  if (!Array.isArray(handles) || !handles.length) return "";
  const first = String(handles[0] || "");
  return /^https?:\/\//i.test(first) ? first : "";
}

export function getCampaignTemplateHeaderText(row) {
  const header = getCampaignTemplateHeaderComponent(row);
  if (!header) return "";
  if (String(header.format || "").toUpperCase() === "TEXT") {
    return header.text || "";
  }
  return header.format ? `[${header.format}]` : "";
}

export function getCampaignTemplateTypeLabel(row) {
  const types = getTemplateComponents(row).map((c) =>
    String(c?.type || "").toUpperCase(),
  );
  if (types.includes("CAROUSEL")) return "Carousel";
  if (types.includes("CATALOG") || row?.templateType === "CATALOG") {
    return "Catalog";
  }
  return "Standard";
}

export function campaignTemplateHasHeader(row) {
  return Boolean(getCampaignTemplateHeaderFormat(row));
}

export function campaignTemplateHasButtons(row) {
  return getTemplateComponents(row).some((c) => {
    const type = String(c?.type || "").toUpperCase();
    return type === "BUTTONS" || type === "BUTTON";
  });
}

function extractVariableIndexesFromText(text) {
  const found = new Set();
  for (const match of String(text || "").matchAll(/\{\{(\d+)\}\}/g)) {
    found.add(match[1]);
  }
  return [...found].sort((a, b) => Number(a) - Number(b));
}

export function extractCampaignBodyVariableIndexes(row) {
  return extractVariableIndexesFromText(getCampaignTemplateBodyText(row));
}

/** Text-header variables only (media headers use a file upload, not this). */
export function extractCampaignHeaderVariableIndexes(row) {
  if (getCampaignTemplateHeaderFormat(row) !== "TEXT") return [];
  const header = getCampaignTemplateHeaderComponent(row);
  return extractVariableIndexesFromText(header?.text || "");
}

export function countCampaignTemplateVariables(row) {
  return (
    extractCampaignBodyVariableIndexes(row).length +
    extractCampaignHeaderVariableIndexes(row).length
  );
}

export function templateStatusClass(status) {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return "wa-status-completed";
  if (s === "PENDING") return "wa-status-pending";
  if (s === "REJECTED") return "wa-status-failed";
  return "wa-status-default";
}

export function buildEmptyVariableMap(indexes) {
  return indexes.reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});
}
